
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout/Layout';
import { useImageVariables } from '@/hooks/useImageVariables';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Search, Eye, Image, Upload, Edit, Trash2, Plus, Save } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { ImageVariable } from '@/hooks/useImageVariables';

const PhotoAlbum = () => {
  const { data: imageVariables = [], refetch } = useImageVariables();
  const [searchFilter, setSearchFilter] = useState('');
  const [selectedImage, setSelectedImage] = useState<ImageVariable | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [editingImage, setEditingImage] = useState<ImageVariable | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    variableName: '',
    description: '',
    altTag: '',
    metaDescription: '',
    keywords: '',
    useCase: ''
  });

  // Only show variables that have images
  const variablesWithImages = imageVariables.filter(v => v.image_url);

  // Filter variables based on search
  const filteredVariables = variablesWithImages.filter(variable => {
    const matchesSearch = variable.variable_name.toLowerCase().includes(searchFilter.toLowerCase()) ||
                         variable.title.toLowerCase().includes(searchFilter.toLowerCase()) ||
                         variable.keywords.some(k => k.toLowerCase().includes(searchFilter.toLowerCase()));
    
    return matchesSearch;
  });

  const handleFileUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file to upload');
      return;
    }

    if (!uploadForm.title || !uploadForm.variableName) {
      toast.error('Please fill in at least the title and variable name');
      return;
    }

    setIsUploading(true);
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result as string;
        const base64 = base64Data.split(',')[1]; // Remove data URL prefix

        const { data, error } = await supabase.functions.invoke('generate-image', {
          body: {
            imageData: base64,
            fileName: uploadFile.name,
            mimeType: uploadFile.type,
            title: uploadForm.title,
            variableName: uploadForm.variableName,
            description: uploadForm.description,
            altTag: uploadForm.altTag || uploadForm.title,
            metaDescription: uploadForm.metaDescription || uploadForm.description,
            keywords: uploadForm.keywords.split(',').map(k => k.trim()).filter(k => k),
            useCase: uploadForm.useCase
          }
        });

        if (error) throw error;

        toast.success('Image uploaded successfully!');
        refetch();
        resetUploadForm();
      };
      reader.readAsDataURL(uploadFile);
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleUpdateImage = async () => {
    if (!editingImage) return;

    try {
      const { error } = await supabase
        .from('image_variables')
        .update({
          title: editingImage.title,
          variable_name: editingImage.variable_name,
          description: editingImage.description,
          alt_tag: editingImage.alt_tag,
          meta_description: editingImage.meta_description,
          keywords: editingImage.keywords,
          use_case: editingImage.use_case,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingImage.id);

      if (error) throw error;

      toast.success('Image updated successfully!');
      refetch();
      setEditingImage(null);
    } catch (error) {
      console.error('Error updating image:', error);
      toast.error('Failed to update image');
    }
  };

  const handleDeleteImage = async (imageId: string) => {
    try {
      const { error } = await supabase
        .from('image_variables')
        .delete()
        .eq('id', imageId);

      if (error) throw error;

      toast.success('Image deleted successfully!');
      refetch();
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Failed to delete image');
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadForm({
      title: '',
      variableName: '',
      description: '',
      altTag: '',
      metaDescription: '',
      keywords: '',
      useCase: ''
    });
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-brand-carolina-blue to-brand-carolina-blue/80 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            📸 Photo Album Management
          </h1>
          <p className="text-xl max-w-3xl mx-auto text-blue-100 mb-8">
            Upload, edit, and manage your roofing project images.
          </p>
        </div>
      </section>

      {/* Controls */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search photos..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
              
              {/* Upload Dialog */}
              <Dialog>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Upload Photo
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Upload New Photo</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="file-upload">Image File</Label>
                      <Input
                        id="file-upload"
                        type="file"
                        accept="image/*"
                        onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="title">Title *</Label>
                      <Input
                        id="title"
                        value={uploadForm.title}
                        onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Image title"
                      />
                    </div>
                    <div>
                      <Label htmlFor="variable-name">Variable Name *</Label>
                      <Input
                        id="variable-name"
                        value={uploadForm.variableName}
                        onChange={(e) => setUploadForm(prev => ({ ...prev, variableName: e.target.value }))}
                        placeholder="variable_name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        value={uploadForm.description}
                        onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Image description"
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label htmlFor="keywords">Keywords (comma-separated)</Label>
                      <Input
                        id="keywords"
                        value={uploadForm.keywords}
                        onChange={(e) => setUploadForm(prev => ({ ...prev, keywords: e.target.value }))}
                        placeholder="roofing, commercial, repair"
                      />
                    </div>
                    <Button 
                      onClick={handleFileUpload} 
                      disabled={isUploading || !uploadFile || !uploadForm.title || !uploadForm.variableName}
                      className="w-full"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {isUploading ? 'Uploading...' : 'Upload Image'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600">
                {variablesWithImages.length} photos in album
              </div>
              <Link to="/gallery" className="text-blue-600 hover:underline">
                View Public Gallery
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Grid */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredVariables.map((variable) => (
              <Card key={variable.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="relative aspect-square bg-gray-100">
                  <img
                    src={variable.image_url}
                    alt={variable.alt_tag}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <CardContent className="p-4">
                  <h3 className="font-semibold truncate mb-1">{variable.title}</h3>
                  <p className="text-sm text-gray-600 truncate mb-2">{variable.variable_name}</p>
                  
                  <div className="flex flex-wrap gap-1 mb-3">
                    {variable.keywords.slice(0, 2).map((keyword, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {keyword}
                      </Badge>
                    ))}
                    {variable.keywords.length > 2 && (
                      <Badge variant="secondary" className="text-xs">
                        +{variable.keywords.length - 2}
                      </Badge>
                    )}
                  </div>

                  <div className="flex gap-2">
                    {/* View Dialog */}
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => setSelectedImage(variable)}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          View
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>{variable.title}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="text-center">
                            <img
                              src={variable.image_url}
                              alt={variable.alt_tag}
                              className="max-h-64 mx-auto rounded-lg object-contain"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <strong>Variable:</strong> {variable.variable_name}
                            </div>
                            <div>
                              <strong>Use Case:</strong> {variable.use_case}
                            </div>
                            <div className="col-span-2">
                              <strong>Description:</strong> {variable.description}
                            </div>
                            <div className="col-span-2">
                              <strong>Keywords:</strong> {variable.keywords.join(', ')}
                            </div>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>

                    {/* Edit Dialog */}
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingImage(variable)}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle>Edit Photo</DialogTitle>
                        </DialogHeader>
                        {editingImage && (
                          <div className="space-y-4">
                            <div>
                              <Label htmlFor="edit-title">Title</Label>
                              <Input
                                id="edit-title"
                                value={editingImage.title}
                                onChange={(e) => setEditingImage(prev => prev ? { ...prev, title: e.target.value } : null)}
                              />
                            </div>
                            <div>
                              <Label htmlFor="edit-variable-name">Variable Name</Label>
                              <Input
                                id="edit-variable-name"
                                value={editingImage.variable_name}
                                onChange={(e) => setEditingImage(prev => prev ? { ...prev, variable_name: e.target.value } : null)}
                              />
                            </div>
                            <div>
                              <Label htmlFor="edit-description">Description</Label>
                              <Textarea
                                id="edit-description"
                                value={editingImage.description}
                                onChange={(e) => setEditingImage(prev => prev ? { ...prev, description: e.target.value } : null)}
                                rows={3}
                              />
                            </div>
                            <div>
                              <Label htmlFor="edit-keywords">Keywords</Label>
                              <Input
                                id="edit-keywords"
                                value={editingImage.keywords.join(', ')}
                                onChange={(e) => setEditingImage(prev => prev ? { 
                                  ...prev, 
                                  keywords: e.target.value.split(',').map(k => k.trim()).filter(k => k)
                                } : null)}
                              />
                            </div>
                            <Button onClick={handleUpdateImage} className="w-full">
                              <Save className="h-4 w-4 mr-2" />
                              Save Changes
                            </Button>
                          </div>
                        )}
                      </DialogContent>
                    </Dialog>

                    {/* Delete Dialog */}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm">
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Photo</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{variable.title}"? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDeleteImage(variable.id)}>
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredVariables.length === 0 && (
            <div className="text-center py-12">
              <Image className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-600 mb-2">No photos found</h3>
              <p className="text-gray-500">
                {searchFilter ? 'Try adjusting your search terms.' : 'Upload your first photo to get started.'}
              </p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default PhotoAlbum;
